import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useShallowAppSelector } from 'store/hooks';
import T from 'components/common/text/T';
import { icon_trash } from 'components/base/images';
import BarChart from 'components/common/chart/BarChart';
import MuiButton from 'components/common/button/MuiButton';
import { color } from 'styles/utils';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import CustomSpan from 'components/common/text/CustomSpan';
import { _color } from 'styles/_variables';
// import 'moment/locale/ko';
// import 'moment/locale/ja';

interface KpiChartBoxProps {
  language: string;
  graphData: any;
  periodRange: any[];
  isMilling: boolean;
  onDelete: (idx: string) => void;
}

function KpiChartBox({ language, graphData, periodRange, isMilling, onDelete }: KpiChartBoxProps) {
  const { t } = useTranslation();
  const [chartData, setChartData] = useState<object[] | []>([]);
  const [kpiChartData, setKpiChartData] = useState<object[] | []>([]);

  const kpiTypes = [
    {
      type: 'PRODUCTIVITY',
      name: '생산성',
      text: '생산량(개)',
      translationCategoryCode: 'KPI_GRAPH_CODE_PRODUCTIVITY',
      translationGraphCode: 'KPI_GRAPH_CODE_PRODUCTIVITY',
    },
    {
      type: 'QUALITY',
      name: '품질',
      text: '불량률(%)',
      translationCategoryCode: 'KPI_CATEGORY_QUALITY',
      translationGraphCode: 'KPI_GRAPH_CODE_QUALITY',
    },
    {
      type: 'DELIVERY',
      name: '납기',
      text: '리드타임(일)',
      translationCategoryCode: 'KPI_CATEGORY_LEADTIME',
      translationGraphCode: 'KPI_GRAPH_CODE_LEADTIME',
    },
    {
      type: 'DELIVERYDEADLINECOMPLIANCE',
      name: '납기 준수율',
      text: '준수율(%)',
      translationCategoryCode: 'KPI_CATEGORY_DELIVERYDEADLINECOMPLIANCE',
      translationGraphCode: 'KPI_GRAPH_CODE_DELIVERYDEADLINECOMPLIANCE',
    },
  ];
  const dataOptionList = [
    { idx: 0, type: 'REMAKE' },
    { idx: 1, type: 'REWORK' },
    { idx: 2, type: 'BOTH' },
  ];
  const rangeType = [
    {
      type: 'M',
      name: 'Monthly',
      text: '월간',
      translationCode: 'GLOBAL_MONTHLY',
    },
    {
      type: 'W',
      name: 'Weekly',
      text: '주간',
      translationCode: 'GLOBAL_WEEKLY',
    },
    {
      type: 'D',
      name: 'Daily',
      text: '일간',
      translationCode: 'GLOBAL_DAILY',
    },
  ];

  const groupOption = [
    {
      type: 'PROJECTCOUNT',
      name: 'Projects',
      translationCode: 'KPI_GROUP_OPTION_PROJECT',
    },
    {
      type: 'TEETHCOUNT',
      name: 'Teeth',
      translationCode: 'KPI_GROUP_OPTION_TEETH',
    },
  ];

  useEffect(() => {
    // periodRange.reverse().forEach((range: string) => {
    if (graphData.duration === 'M') {
      periodRange.forEach((range: { week?: number | null; date: string }) => {
        const findData = graphData.list.find((item: any) => item.groupDate === range.date);
        // const groupMonth = moment(range.date).locale(language).format('MMM');
        const groupMonth = moment(range.date).locale(language).format('MMM');
        const totalCount: number = !!findData ? findData.value : 0;
        const groupData: { x: string; y: string; graphValue: string } = {
          x: groupMonth,
          y: `${totalCount}`,
          graphValue: `${totalCount}`,
        };
        setChartData((draft: object[]) => [...draft, { ...groupData }]);
      });
    }

    if (graphData.duration === 'W') {
      console.log('HDS] language ___ ', language);
      periodRange.forEach((range: { week: number | null; date: string }) => {
        const findData = graphData.list.find((item: any) => item.groupDate === range.week);
        const groupMonth: string = moment(range.date)
          .locale(language)
          .format(language === 'en' ? 'MMM DD' : 'MM/DD');
        const totalCount: number = !!findData ? findData.value : 0;
        const groupData: { x: string; y: string; graphValue: string } = {
          x: groupMonth,
          y: `${totalCount}`,
          graphValue: `${totalCount}`,
        };
        setChartData((draft: object[]) => [...draft, { ...groupData }]);
      });
    }

    if (graphData.duration === 'D') {
      periodRange.forEach((range: { week?: number | null; date: string }) => {
        const findData = graphData.list.find((item: any) => item.groupDate === range.date);
        const groupDay: string = moment(range.date)
          .locale(language)
          .format(language === 'en' ? 'MMM DD' : 'MM/DD');
        const totalCount: number = !!findData ? findData.value : 0;
        const groupData: { x: string; y: string; graphValue: string } = {
          x: groupDay,
          y: `${totalCount}`,
          graphValue: `${totalCount}`,
        };
        setChartData((draft: object[]) => [...draft, { ...groupData }]);
      });
    }
  }, []);

  useEffect(() => {
    if (chartData.length > 0) {
      const animation = setTimeout(() => setKpiChartData(chartData), 1);
      return () => {
        clearTimeout(animation);
      };
    }
  }, [chartData]);

  return (
    <StyledKpiChartBox data-component-name="KpiChart">
      <h1 className="sr-only">KPI Graph</h1>
      <div className="KpiChart__info_container">
        {isMilling ? (
          <CustomSpan
            fontColor={_color.blue}
            className="KpiChart__delete_button"
          >{`[MILLING]`}</CustomSpan>
        ) : (
          <MuiButton
            config={{
              width: 120,
              borderColor: color.gray_b5,
              bgColor: 'white',
            }}
            variant="outlined"
            className="sm KpiChart__delete_button"
            startIcon={<img src={icon_trash} />}
            onClick={() => onDelete(graphData._id)}
          >
            <T>GLOBAL_DELETE</T>
          </MuiButton>
        )}
        <div className="KpiChart__info_row type">
          <div className="KpiChart__info_row_label">
            <T>GLOBAL_CATEGORY</T>
          </div>
          <div className="KpiChart__info_row_contents">
            <T>{kpiTypes.find(item => item.type === graphData.type)?.translationCategoryCode}</T>
          </div>
        </div>
        <div className="KpiChart__info_row client">
          <div className="KpiChart__info_row_label">
            <T>GLOBAL_CLIENT</T>
          </div>
          <div className="KpiChart__info_row_contents">{graphData.clientName}</div>
        </div>
        <div className="KpiChart__info_row meterial">
          <div className="KpiChart__info_row_label">
            <T>GLOBAL_MATERIAL</T>
          </div>
          <div className="KpiChart__info_row_contents">{graphData.material}</div>
        </div>
        <div className="KpiChart__info_row abutment">
          <div className="KpiChart__info_row_label">
            <T>GLOBAL_ABUTMENT</T>
          </div>
          <div className="KpiChart__info_row_contents">{graphData.implant}</div>
        </div>
      </div>
      <div className="KpiChart__chart_container">
        <div className="KpiChart__chart_row">
          <div className="KpiChart__chart_row_label">
            <T>{rangeType.find(item => item.type === graphData.duration)?.translationCode}</T>{' '}
            <T>{kpiTypes.find(item => item.type === graphData.type)?.translationGraphCode}</T>
            {graphData.dataOption === 'BOTH' && (
              <span className="chart_data_option">
                <T>KPI_GRAPH_INCLUDE_REWORK</T>
              </span>
            )}
          </div>
          <div className="KpiChart__chart_row_contents">
            <div className="KpiChart__contents_legends"></div>
            {isMilling ? (
              `가공`
            ) : (
              <T>
                {groupOption.find(item => item.type === graphData.groupOption)?.translationCode}
              </T>
            )}
            {' ( '}
            <T>KPI_GROUP_UNIT</T>
            {' : '}
            <T>{rangeType.find(item => item.type === graphData.duration)?.translationCode}</T>
            {' )'}
          </div>
        </div>
        <BarChart
          width="100%"
          height={440}
          data={kpiChartData}
          // padding={0.9}
          padding={0.9}
          colors={'#04a4e3'}
          margin={{ top: 5, right: 5, bottom: 30, left: 40 }}
          axisBottom={{
            tickSize: 0,
            tickPadding: 15,
            tickRotation: 0,
            legendPosition: 'middle',
          }}
          className="salesPoint__chart"
          // maxValueRange={graphData.type === 'QUALITY' ? 100 : null}
        />
      </div>
    </StyledKpiChartBox>
  );
}
const StyledKpiChartBox = styled.section<{}>`
  padding: 40px;

  height: 830px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  .KpiChart__info_container {
    display: flex;
    flex-direction: column;
    row-gap: 20px;
    position: relative;
    .KpiChart__delete_button {
      position: absolute;
      top: 0;
      right: 0;
    }
    .KpiChart__info_row {
      font-size: 14px;
      display: flex;

      .KpiChart__info_row_label {
        font-weight: 500;
        width: 120px;
        display: flex;
        align-items: center;
      }
      .KpiChart__info_row_contents {
        width: calc(100% - 120px);
        line-height: 20px;
        display: flex;
        align-items: center;
      }
    }
  }
  .KpiChart__chart_container {
    .KpiChart__chart_row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      .KpiChart__chart_row_label {
        font-size: 18px;
        font-weight: 500;
        display: flex;
        align-items: center;
        .chart_data_option {
          margin-left: 10px;
          width: 110px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          background-color: #00a6e2;
          color: #fff;
          border-radius: 2px;
          font-weight: normal;
        }
      }
      .KpiChart__chart_row_contents {
        font-size: 14px;
        font-weight: 500;
        display: flex;
        align-items: center;
        color: #00a6e2;
        .KpiChart__contents_legends {
          width: 26px;
          height: 10px;
          border-radius: 5px;

          /* background: linear-gradient(90deg, rgba(8, 123, 238, 1) 0%, rgba(0, 166, 226, 1) 100%); */
          background: linear-gradient(90deg, rgba(22, 45, 141, 1) 0%, rgba(0, 166, 226, 1) 100%);
          margin-right: 10px;
        }
      }
    }
  }
`;
export default React.memo(KpiChartBox);
